const DB_NAME = "UserDeviceIDDB";
const STORE_NAME = "UserDeviceID";
const DB_VERSION = 1;

interface UserDevice {
  username: string;
  deviceID: string;
}

function isIndexedDBSupported(): boolean {
  return typeof indexedDB !== "undefined";
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "username" });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function generateDeviceId() {
  // return 'fp-' + hash.toString(16);
  // Check if we already have a stored device ID
  let deviceId = localStorage.getItem('deviceid');

  if (!deviceId) {
    // Create a fingerprint based on browser characteristics
    const info = [
      navigator.userAgent,
      navigator.language,
      screen.colorDepth,
      screen.width,
      screen.height,
      new Date().toISOString()
    ].join('|');

    // Simple hash function (FNV-1a for example)
    let hash = 0x811c9dc5;
    for (let i = 0; i < info.length; i++) {
      hash ^= info.charCodeAt(i);
      hash = (hash * 0x01000193) >>> 0;
    }

    // Create a hash of the fingerprint
    deviceId = 'web' + (hash).toString().replace(/[^a-zA-Z0-9]/g, '').substr(0, 16);
  }

  return deviceId;
}

export async function storeDeviceID(username: string): Promise<void> {
  const deviceID = generateDeviceId();
  const data: UserDevice = { username, deviceID };

  if (isIndexedDBSupported()) {
    try {
      const db = await openDB();
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      store.put(data);
      await new Promise<void>((resolve, reject) => {
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error);
      });
      db.close();
      return;
    } catch (err) {
      console.warn("IndexedDB failed, falling back to sessionStorage", err);
    }
  }

  // Fallback to sessionStorage
  sessionStorage.setItem(`user-device-id-${username}`, JSON.stringify(data));
}

export async function getDeviceID(username: string): Promise<string | null> {
  if (isIndexedDBSupported()) {
    try {
      const db = await openDB();
      const tx = db.transaction(STORE_NAME, "readonly");
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(username);

      return new Promise((resolve, reject) => {
        request.onsuccess = () => {
          const result = request.result as UserDevice | undefined;
          resolve(result?.deviceID || null);
        };
        request.onerror = () => reject(request.error);
      });
    } catch (err) {
      console.warn("IndexedDB get failed, falling back to sessionStorage", err);
    }
  }

  // Fallback to sessionStorage
  const raw = sessionStorage.getItem(`user-device-id-${username}`);
  if (!raw) return null;
  try {
    const data: UserDevice = JSON.parse(raw);
    return data.deviceID || null;
  } catch {
    return null;
  }
}
